import { checkObsStatus, mapObservationFromAPI, obsTypes } from '../../pages/ward/forms/observation/obsUtils';
import moment from 'moment';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import config from '../../app/config';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import API from '../../app/api';
import InformationBox from '../other/informationBox';
import clsx from 'clsx';
import { getTableScrollbar } from '../../util/scrollbar';
import { getCellValue } from '../../pages/ward/forms/plans/abbeyPainScaleFormObs';

const useStyles = makeStyles((theme) => ({
    table: {
        width: '100%',
    },
    headCell: {
        fontSize: '13px',
        background: theme.palette.background.default,
        whiteSpace: 'nowrap',
    },
    rowCell: {
        fontSize: 'small',
        whiteSpace: 'nowrap',
    },
}));

const ObservationTable = ({ patient, specificObs, chooseObs, chosenObs, setChosenObs, abbeyPainScale, apsLastObs }) => {
    const classesLocal = useStyles();
    const { t } = useTranslation();
    const controller = new AbortController();

    // States
    const [obs, setObs] = useState(null);
    const [obsAll, setObsAll] = useState(null);
    const memoizedPatient = useMemo(() => patient, [patient]);
    const memoizedChosenObs = useMemo(() => chosenObs, [chosenObs]);
    const [abbeyPainScaleData, setAbbeyPainScaleData] = useState(patient?.studies?.abbeyPainScaleForm || []);

    const age = moment().diff(patient?.patient?.dob, 'years');
    const isRetroObs = config?.observations?.retrospective || false;

    useEffect(() => {
        return () => {
            controller.abort();
        };
    }, []);

    useEffect(() => {
        setAbbeyPainScaleData(patient?.studies?.abbeyPainScaleForm || []);
    }, [patient?.studies?.abbeyPainScaleForm]);

    const getObservationsFromAPI = useCallback(
        async ({ newType, amount }) => {
            try {
                API.getFullObservations(patient, newType, true, controller.signal).then((response) => {
                    let ob = [];
                    _.forEach(response, (apiObs) => {
                        ob.push(mapObservationFromAPI(apiObs));
                    });
                    if (ob.length === 0) return null;
                    amount === 'last' && ob.length > 0 && setObs(ob[ob.length - 1]);
                    amount === 'all' && ob.length > 0 && setObsAll(ob);
                });
            } catch (err) {
                console.error('Observations.API.getObservations', err);
            }
        },
        [memoizedPatient],
    );

    useEffect(async () => {
        if (memoizedPatient) {
            const type = age >= 16 ? obsTypes.news : obsTypes.pews;

            if (specificObs) {
                // obs = specificObs;
                setObs(specificObs);
            } else {
                // obs = patient.patient.lastObservation ? mapObservationFromAPI(patient.patient.lastObservation) : getObservationsFromAPI(type) || null;
                // patient.patient?.lastObservation ? setObs(mapObservationFromAPI(patient.patient.lastObservation)) : await getObservationsFromAPI({newType: type, amount: 'last'});
                chooseObs && (await getObservationsFromAPI({ newType: type, amount: 'all' }));
                await getObservationsFromAPI({ newType: type, amount: 'last' });
            }
        }
    }, [memoizedPatient, specificObs, chooseObs]);

    if (!patient) {
        console.error('[ObservationTable] Not patient provided');
        return null;
    }

    const getVal = (val) => {
        return _.isNil(val) ? '-' : val;
    };

    const getPulseValue = (pulseRate, pulseType) => {
        let pType = '';
        switch (pulseType) {
            case 'irregular':
                pType = '(IR)';
                break;
            case 'regular':
                pType = '(RE)';
                break;
            default:
                pType = '';
        }

        return `${pulseRate} ${pType}`;
    };

    const isObsChecked = (z) => {
        return memoizedChosenObs?.some((chosenOb) => chosenOb.__id === z.__id);
    };

    const handleToggleCheckbox = (z) => {
        const currentIndex = chosenObs.findIndex((x) => x.__id === z.__id);
        const newChecked = [...chosenObs];

        if (currentIndex === -1) {
            newChecked.push(z);
        } else {
            newChecked.splice(currentIndex, 1);
        }
        setChosenObs(newChecked);
    };

    const renderAllObsTable = () => {
        return (
            obsAll &&
            chosenObs &&
            obsAll.map((ob) => {
                const respirationOb = checkObsStatus(ob.respiration);
                const airOxygenOb = checkObsStatus(ob.airOxygen);
                const spo2scaleOb = checkObsStatus(ob.spo2scale);
                const pulseRateOb = checkObsStatus(ob.pulseRate);
                const temperatureOb = checkObsStatus(ob.temperature);
                const bloodPressureOb = checkObsStatus(ob.bloodPressure);
                const consciousnessOb = checkObsStatus(ob.consciousness);
                const painScoreOb = checkObsStatus(ob.painScore);
                const distressOb = checkObsStatus(ob.distress);
                const wheezingOb = checkObsStatus(ob.wheezing);
                const stridorOb = checkObsStatus(ob.stridor);
                const apnoeaOb = checkObsStatus(ob.apnoea);
                const crtOb = checkObsStatus(ob.crt);
                const glucoseOb = checkObsStatus(ob.glucose);

                return (
                    <TableRow key={ob.__id} onClick={handleToggleCheckbox.bind(this, ob)}>
                        <TableCell align="center" size="small" className={classesLocal.rowCell}>
                            <Checkbox
                                checked={isObsChecked(ob)}
                                inputProps={{ 'aria-label': 'controlled' }}
                                size="small"
                                align="center"
                                onChange={handleToggleCheckbox.bind(this, ob)}
                            />
                        </TableCell>
                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                            {ob?.startedDate ? moment(ob.startedDate).format(config.defaultDateTimeFormat) : '-'}
                        </TableCell>

                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                            {ob?.totalScore}
                        </TableCell>

                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                            {respirationOb === 'completed' ? getVal(ob?.respiration?.breaths) : respirationOb}
                        </TableCell>

                        {age < 18 && (
                            <Fragment>
                                <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                    {distressOb === 'completed' ? getVal(ob?.distress?.distress?.description) : distressOb}
                                </TableCell>

                                <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                    {wheezingOb === 'completed' ? getVal(ob?.wheezing?.wheezing?.description) : wheezingOb}
                                </TableCell>

                                <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                    {stridorOb === 'completed' ? (ob?.stridor?.stridor ? 'Yes' : 'No') : stridorOb}
                                </TableCell>

                                <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                    {apnoeaOb === 'completed' ? (ob?.apnoea?.apnoea ? 'Yes' : 'No') : apnoeaOb}
                                </TableCell>
                            </Fragment>
                        )}

                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                            {airOxygenOb === 'completed' ? _.capitalize(getVal(ob?.airOxygen?.airType)) : airOxygenOb}
                        </TableCell>

                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                            {spo2scaleOb === 'completed' ? getVal(ob?.spo2scale?.spo2scale) : spo2scaleOb}
                        </TableCell>

                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                            {bloodPressureOb === 'completed' ? getVal(ob?.bloodPressure?.systolicPressure) : bloodPressureOb}
                        </TableCell>

                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                            {bloodPressureOb === 'completed' ? getVal(ob?.bloodPressure?.diastolicPressure) : bloodPressureOb}
                        </TableCell>

                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                            {pulseRateOb === 'completed'
                                ? getVal(getPulseValue(ob?.pulseRate?.pulseRate, ob?.pulseRate?.pulseType))
                                : pulseRateOb}
                        </TableCell>

                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                            {consciousnessOb === 'completed' ? getVal(ob?.consciousness?.consciousnessLevel?.description) : consciousnessOb}
                        </TableCell>

                        {age < 18 && (
                            <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                {crtOb === 'completed' ? getVal(ob?.crt?.crt) : crtOb}
                            </TableCell>
                        )}
                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                            {temperatureOb === 'completed' ? getVal(ob?.temperature?.temperature) : temperatureOb}
                        </TableCell>

                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                            {painScoreOb === 'completed' ? getVal(ob?.painScore?.painScore) : painScoreOb}
                        </TableCell>

                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                            {glucoseOb === 'completed' ? getVal(ob?.glucose?.glucose) : glucoseOb}
                        </TableCell>

                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                            {glucoseOb === 'completed' ? getVal(ob.glucose.ketones) : glucoseOb}
                        </TableCell>

                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                            {obs.completedDate ? moment(ob?.completedDate).format(config.defaultDateTimeFormat) : '-'}
                        </TableCell>

                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                            {getVal(ob?.completedByFullName)}
                        </TableCell>

                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                            {ob?.extraOption3 ? 'Yes' : 'No'}
                        </TableCell>
                    </TableRow>
                );
            })
        );
    };

    const renderAbbeyPainScaleTable = () => {
        const labels = [
            'APS Score',
            'Vocalisation',
            'Facial expression',
            'Change in body language',
            'Behavioural',
            'Physiological',
            'Physical',
            'Type of pain',
            'By',
        ];
        const abbeyField = [
            'total',
            'vocalisation',
            'facialExpression',
            'changeInBody',
            'behavioural',
            'physiological',
            'physical',
            'typeOfPain',
            'initials1',
        ];

        return (
            <Fragment>
                <TableHead>
                    <TableRow>
                        {labels.map((el, index) => (
                            <TableCell key={index} size={'small'} className={classesLocal.headCell} align={'center'}>
                                {el}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                {(apsLastObs ? [abbeyPainScaleData.at(-1)] : abbeyPainScaleData).map((property, rowIndex) => (
                    <TableRow key={rowIndex}>
                        {abbeyField.map((label, colIndex) => {
                            const value = getCellValue(label, property[label]);
                            return (
                                <TableCell key={colIndex} size={'small'} className={classesLocal.rowCell} align={'center'}>
                                    {value}
                                </TableCell>
                            );
                        })}
                    </TableRow>
                ))}
            </Fragment>
        );
    };

    if (abbeyPainScale) {
        if (abbeyPainScaleData?.length <= 0) {
            return <InformationBox type={'info'}>{t('Abbey Pain Scale not made.')}</InformationBox>;
        } else {
            return (
                <Fragment>
                    <Grid item xs={12}>
                        {apsLastObs ? <h3>{t('Last Abbey Pain Scale Observation')}</h3> : <h3>{t('Abbey Pain Scale Observations')}</h3>}
                        <Table sx={{ minWidth: 650 }}>{renderAbbeyPainScaleTable()}</Table>
                    </Grid>
                </Fragment>
            );
        }
    }

    if (obs) {
        const respiration = checkObsStatus(obs.respiration);
        const airOxygen = checkObsStatus(obs.airOxygen);
        const spo2scale = checkObsStatus(obs.spo2scale);
        const pulseRate = checkObsStatus(obs.pulseRate);
        const temperature = checkObsStatus(obs.temperature);
        const bloodPressure = checkObsStatus(obs.bloodPressure);
        const consciousness = checkObsStatus(obs.consciousness);
        const painScore = checkObsStatus(obs.painScore);
        const distress = checkObsStatus(obs.distress);
        const wheezing = checkObsStatus(obs.wheezing);
        const stridor = checkObsStatus(obs.stridor);
        const apnoea = checkObsStatus(obs.apnoea);
        const crt = checkObsStatus(obs.crt);
        const glucose = checkObsStatus(obs.glucose);

        return (
            <Fragment>
                <Grid item xs={12}>
                    {!specificObs && !chooseObs && <h3>{t('Last Observation')}</h3>}

                    {chooseObs && (
                        <Table sx={{ minWidth: 650 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell className={classesLocal.headCell} align="center">
                                        {t('Add')}
                                    </TableCell>
                                    <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                        {t(isRetroObs ? 'Assessed' : 'Started')}
                                    </TableCell>

                                    <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                        {t(`${age >= 16 ? 'NEWS' : 'PEWS'} Score`)}
                                    </TableCell>

                                    <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                        {t('Resp Rate')}
                                    </TableCell>

                                    {age < 16 && (
                                        <Fragment>
                                            <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                                {t('Resp Distress')}
                                            </TableCell>

                                            <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                                {t('Wheezing')}
                                            </TableCell>

                                            <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                                {t('Stridor')}
                                            </TableCell>

                                            <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                                {t('Apnoea')}
                                            </TableCell>
                                        </Fragment>
                                    )}

                                    <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                        {t('Inspired O₂%')}
                                    </TableCell>

                                    <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                        {t(`SpO₂${obs.spo2scale?.hypercapnic ? ' Scale 2' : ''}`)}
                                    </TableCell>

                                    <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                        {t('BP Systolic')}
                                    </TableCell>

                                    <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                        {t('BP Diastolic')}
                                    </TableCell>

                                    <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                        {t('Pulse')}
                                    </TableCell>

                                    <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                        {t('Conscious')}
                                    </TableCell>

                                    {age < 16 && (
                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t('CRT')}
                                        </TableCell>
                                    )}

                                    <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                        {t('Temp')}
                                    </TableCell>

                                    <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                        {t('Pain Score')}
                                    </TableCell>

                                    <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                        {t('Blood Glucose')}
                                    </TableCell>

                                    <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                        {t('Ketones')}
                                    </TableCell>

                                    <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                        {t(isRetroObs ? 'Submitted' : 'Completed')}
                                    </TableCell>

                                    <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                        {t('By')}
                                    </TableCell>

                                    <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                        {t('Sepsis Suspected')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>{renderAllObsTable()}</TableBody>
                        </Table>
                    )}

                    {!chooseObs && (
                        <TableContainer component={Paper} className={clsx(getTableScrollbar(), classesLocal.table)}>
                            <Table sx={{ minWidth: 650 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t(isRetroObs ? 'Assessed' : 'Started')}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t(`${age >= 16 ? 'NEWS' : 'PEWS'} Score`)}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t('Resp Rate')}
                                        </TableCell>

                                        {age < 16 && (
                                            <Fragment>
                                                <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                                    {t('Resp Distress')}
                                                </TableCell>

                                                <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                                    {t('Wheezing')}
                                                </TableCell>

                                                <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                                    {t('Stridor')}
                                                </TableCell>

                                                <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                                    {t('Apnoea')}
                                                </TableCell>
                                            </Fragment>
                                        )}

                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t('Inspired O₂%')}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t(`SpO₂${obs.spo2scale?.hypercapnic ? ' Scale 2' : ''}`)}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t('BP Systolic')}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t('BP Diastolic')}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t('Pulse')}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t('Conscious')}
                                        </TableCell>

                                        {age < 16 && (
                                            <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                                {t('CRT')}
                                            </TableCell>
                                        )}

                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t('Temp')}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t('Pain Score')}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t('Blood Glucose')}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t('Ketones')}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t(isRetroObs ? 'Submitted' : 'Completed')}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t('By')}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.headCell} align={'center'}>
                                            {t('Sepsis Suspected')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    <TableRow>
                                        {chooseObs && (
                                            <TableCell align="center" size="small" className={classesLocal.rowCell}>
                                                <Checkbox checked size="small" align="center" />
                                            </TableCell>
                                        )}
                                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                            {obs.startedDate ? moment(obs.startedDate).format(config.defaultDateTimeFormat) : '-'}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                            {obs.totalScore}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                            {respiration === 'completed' ? getVal(obs.respiration.breaths) : respiration}
                                        </TableCell>

                                        {age < 16 && (
                                            <Fragment>
                                                <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                                    {distress === 'completed' ? getVal(obs.distress?.distress?.description) : distress}
                                                </TableCell>

                                                <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                                    {wheezing === 'completed' ? getVal(obs.wheezing?.wheezing?.description) : wheezing}
                                                </TableCell>

                                                <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                                    {stridor === 'completed' ? (obs.stridor?.stridor ? 'Yes' : 'No') : stridor}
                                                </TableCell>

                                                <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                                    {apnoea === 'completed' ? (obs.apnoea?.apnoea ? 'Yes' : 'No') : apnoea}
                                                </TableCell>
                                            </Fragment>
                                        )}

                                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                            {airOxygen === 'completed' ? _.capitalize(getVal(obs.airOxygen.airType)) : airOxygen}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                            {spo2scale === 'completed' ? getVal(obs.spo2scale.spo2scale) : spo2scale}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                            {bloodPressure === 'completed' ? getVal(obs.bloodPressure.systolicPressure) : bloodPressure}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                            {bloodPressure === 'completed' ? getVal(obs.bloodPressure.diastolicPressure) : bloodPressure}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                            {pulseRate === 'completed'
                                                ? getVal(getPulseValue(obs.pulseRate.pulseRate, obs.pulseRate.pulseType))
                                                : pulseRate}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                            {consciousness === 'completed'
                                                ? getVal(obs.consciousness.consciousnessLevel?.description)
                                                : consciousness}
                                        </TableCell>

                                        {age < 16 && (
                                            <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                                {crt === 'completed' ? getVal(obs.crt.crt) : crt}
                                            </TableCell>
                                        )}
                                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                            {temperature === 'completed' ? getVal(obs.temperature.temperature) : temperature}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                            {painScore === 'completed' ? getVal(obs.painScore.painScore) : painScore}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                            {glucose === 'completed' ? getVal(obs.glucose.glucose) : glucose}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                            {glucose === 'completed' ? getVal(obs.glucose.ketones) : glucose}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                            {obs.completedDate ? moment(obs.completedDate).format(config.defaultDateTimeFormat) : '-'}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                            {getVal(obs.completedByFullName)}
                                        </TableCell>

                                        <TableCell size={'small'} className={classesLocal.rowCell} align={'center'}>
                                            {obs.extraOption3 ? 'Yes' : 'No'}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Grid>
            </Fragment>
        );
    } else {
        return <InformationBox type={'info'}>{t('Observations not made.')}</InformationBox>;
    }
};

export default ObservationTable;
